import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import axios from "axios";
import { useDispatch } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { useGlobalAuthContext } from "../../context/AuthContext";
import { useUserProfileSelector } from "../../hooks/useUserProfile";
import { DynamicAlert } from "../../utils/Alerts";
import { apiRoomNate } from "../../Slices/roomNateApis";
import { setUserProfile } from "../../Slices/roomNateSlice";
import DragAndDropFile from "../Account/drag-and-drop";
import { getAuthorizationHeaderObject } from "../../utils/authHeaders";
import { resolveUserBrandingLogo } from "../../utils/resolveBrandingLogo";
import {
  getAuthGlassFieldSx,
  getAuthPrimaryButtonSx,
} from "../Auth/authGlassStyles";
import { shapeShell } from "../../theme/shape";

/**
 * Branding editor: logo upload and disclaimer.
 * Opened from the /main header menu → "Branding".
 *
 * Two logo zones (independent):
 *  - Admin / partner logo (`profile.partnerLogo`) — read-only display only,
 *    set by the master admin via the partner settings modal. Hidden when
 *    the partner has no logo configured.
 *  - User logo — editable via `resolveUserBrandingLogo(profile)`. Always rendered;
 *    shows the drop-zone hint when empty. Replace by dropping or tapping the
 *    preview; remove via the "Remove image" affordance below the zone.
 */
export default function UserProfileEditDialog({ open, onClose }) {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));
  const fieldSx = getAuthGlassFieldSx(theme);
  const dispatch = useDispatch();
  const { currentUserSessionStorage, partnerName } = useGlobalAuthContext();
  const { userProfile: profile } = useUserProfileSelector();

  const logoFileInputRef = React.useRef(null);

  const [logoId, setLogoId] = React.useState(null);
  const [logoCleared, setLogoCleared] = React.useState(false);
  const [load, setLoad] = React.useState(false);
  const [title, setTitle] = React.useState("");
  const [status, setStatus] = React.useState(false);
  const [clearingLogo, setClearingLogo] = React.useState(false);
  const [brandingDropKey, setBrandingDropKey] = React.useState(0);

  const authHeaders = () =>
    getAuthorizationHeaderObject(currentUserSessionStorage);

  const refreshProfileFromServer = React.useCallback(async () => {
    const { data } = await axios.get("user/getLoggedInUser", {
      headers: getAuthorizationHeaderObject(currentUserSessionStorage),
    });
    if (data) dispatch(setUserProfile(data));
    return data;
  }, [currentUserSessionStorage, dispatch]);

  // Admin / partner logo is strictly `profile.partnerLogo` here. This dialog
  // does NOT use `resolveBrandingLogo` because we want a clean visual split:
  // the admin zone shows the partner-admin upload, the user zone shows the
  // user's own upload, even when both are set. (App-wide rendering still
  // goes through `resolveBrandingLogo` and keeps the existing precedence.)
  const adminLogoUrl =
    typeof profile?.partnerLogo === "string" && profile.partnerLogo.length > 0
      ? profile.partnerLogo
      : null;

  // `logoCleared` is the user's intent within this dialog session. Honor it
  // over Redux so the drop zone shows empty even if a backend race briefly
  // restores the old URL after a PUT.
  const rawUserLogo = resolveUserBrandingLogo(profile);

  const hasUserLogo = Boolean(rawUserLogo) && !logoCleared;
  const userLogoForDropZone = hasUserLogo ? rawUserLogo : null;

  const adminZoneLabel = partnerName
    ? `${partnerName} Logo`
    : "Partner Logo";

  // Reset session-only flags on every open so a prior session can't leak in.
  React.useEffect(() => {
    if (open) {
      setLogoCleared(false);
      setLogoId(null);
    }
  }, [open]);

  const handleSetLogoId = React.useCallback((id) => {
    setLogoId(id);
    if (id) setLogoCleared(false);
  }, []);

  const handleClearBranding = async () => {
    if (!rawUserLogo || clearingLogo || logoCleared) return;
    setClearingLogo(true);
    setLogoId(null);
    setLogoCleared(true);
    setBrandingDropKey((k) => k + 1);
    try {
      await axios.put(
        "/user/update/",
        { logo: null },
        { headers: authHeaders() }
      );
      dispatch(apiRoomNate.util.invalidateTags(["UserProfile"]));
      await refreshProfileFromServer();
    } catch (error) {
      // Roll the intent back so the UI matches what's actually persisted.
      setLogoCleared(false);
      setTitle(
        typeof error?.response?.data === "string"
          ? error.response.data
          : "Could not remove logo."
      );
      setStatus(false);
      setTimeout(() => setTitle(""), 2500);
    } finally {
      setClearingLogo(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      disclaimer: profile?.disclaimer || "",
      role: "user",
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoad(true);
      try {
        // Only include `logo` on the PUT when the user explicitly uploaded or
        // cleared. Sending `logo: null` on a disclaimer-only save would let a
        // permissive backend silently wipe the logo.
        const payload = {
          disclaimer: values.disclaimer,
          role: values.role,
        };
        if (logoId) payload.logo = logoId;
        else if (logoCleared) payload.logo = null;

        await axios.put("/user/update/", payload, {
          headers: authHeaders(),
        });

        dispatch(apiRoomNate.util.invalidateTags(["UserProfile"]));
        await refreshProfileFromServer();

        setLoad(false);
        setLogoId(null);
        setLogoCleared(false);
        onClose();
      } catch (error) {
        setLoad(false);
        setTitle(
          typeof error?.response?.data === "string"
            ? error.response.data
            : "Could not save changes."
        );
        setStatus(false);
        setTimeout(() => setTitle(""), 2000);
      }
    },
  });

  const resetAndClose = () => {
    setLogoId(null);
    setLogoCleared(false);
    formik.resetForm();
    setBrandingDropKey((k) => k + 1);
    onClose();
  };

  const handleDialogClose = (_event, reason) => {
    if (load) return;
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      resetAndClose();
    }
  };

  return (
    <>
      {title?.length >= 1 && <DynamicAlert status={status} title={title} />}

      <Dialog
        open={open}
        onClose={handleDialogClose}
        fullScreen={fullScreenDialog}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        aria-labelledby="user-profile-edit-dialog-title"
          sx={{
          "& .MuiDialog-paper": {
            ...(fullScreenDialog && { maxHeight: "100dvh" }),
            ...(!fullScreenDialog && { borderRadius: shapeShell }),
          },
          "& .MuiDialogContent-root": {
            WebkitOverflowScrolling: "touch",
          },
        }}
      >
        <DialogTitle
          id="user-profile-edit-dialog-title"
          sx={{
            fontWeight: 600,
            letterSpacing: "-0.02em",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: { xs: 2.5, sm: 3 },
          }}
        >
          Branding
          <IconButton
            aria-label="Close"
            onClick={resetAndClose}
            disabled={load}
            size="small"
            sx={{ color: "text.secondary", ml: 1.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 2, sm: 3 },
            pb: {
              xs: "max(env(safe-area-inset-bottom, 0px) + 24px, 24px)",
              sm: 3,
            },
            pt: 0,
          }}
        >
          {/* Admin / partner logo — read-only. Rendered only when the
              partner admin has actually set a logo. No upload, change, or
              remove controls; this is purely display for the user. */}
          {adminLogoUrl && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  alignSelf: "flex-start",
                  color: "text.secondary",
                  fontWeight: 500,
                  mb: 0.75,
                }}
              >
                {adminZoneLabel}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  minHeight: 148,
                  maxHeight: 200,
                  borderRadius: "14px",
                  border: "1px solid rgba(0,0,0,0.09)",
                  backgroundColor: "rgba(0,0,0,0.02)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  boxSizing: "border-box",
                  px: 1,
                  py: 1.5,
                }}
              >
                <Box
                  component="img"
                  src={adminLogoUrl}
                  alt={adminZoneLabel}
                  draggable={false}
                  sx={{
                    display: "block",
                    maxWidth: "100%",
                    maxHeight: 168,
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Box>
          )}

          {/* User's personal logo — editable. Always rendered. The drop
              zone is itself the affordance (drop a file or tap the preview);
              "Replace image" / "Change image" buttons have been removed. */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                alignSelf: "flex-start",
                color: "text.secondary",
                fontWeight: 500,
                mb: 0.75,
              }}
            >
              My Logo
            </Typography>
            <Box
              sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                overflow: "visible",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <DragAndDropFile
                ref={logoFileInputRef}
                key={`${brandingDropKey}-${userLogoForDropZone ?? "empty"}`}
                logo={userLogoForDropZone}
                logoSurfaceShape="rectangle"
                preventCall={true}
                setLogoId={handleSetLogoId}
                hideUploadButton={true}
                previewClickable={true}
              />
            </Box>

            {/* Remove image — only when a saved user logo exists and no
                new upload is staged. Reserves vertical space so the layout
                does not jump when the button toggles in/out. */}
            <Stack
              direction="row"
              gap={0.5}
              mt={1}
              sx={{
                visibility:
                  hasUserLogo && !logoId ? "visible" : "hidden",
              }}
            >
              <Button
                type="button"
                variant="text"
                size="small"
                disabled={clearingLogo}
                onClick={handleClearBranding}
                sx={{
                  color: "text.disabled",
                  fontSize: "0.75rem",
                  textTransform: "none",
                  minHeight: 0,
                  py: "4px",
                  px: 1,
                  "&:hover": { color: "text.secondary", bgcolor: "transparent" },
                }}
              >
                {clearingLogo ? "Removing…" : "Remove image"}
              </Button>
            </Stack>
          </Box>

          {/* Disclaimer + Save — always 16px below the user-logo row */}
          <Stack
            gap={2}
            component="form"
            onSubmit={formik.handleSubmit}
            sx={{ mt: 2 }}
          >
            <TextField
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              label="Disclaimer"
              id="disclaimer"
              inputMode="text"
              value={formik.values.disclaimer}
              placeholder="Optional: Add a disclaimer to your exported images"
              name="disclaimer"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              InputLabelProps={{ shrink: true }}
              sx={fieldSx}
            />

            <Box mt={1}>
              <LoadingButton
                loading={load}
                variant="contained"
                fullWidth
                type="submit"
                sx={getAuthPrimaryButtonSx(theme)}
              >
                Save branding
              </LoadingButton>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}

UserProfileEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
